// /frontend/src/Pages/Employer/Home.jsx
// Employer dashboard – shows only employer's jobs and basic stats.

import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import API from "../../helpers/API";
import { EditOutlined, DeleteOutlined, EyeOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import $ from "jquery";
import "bootstrap/dist/js/bootstrap.bundle.min";
import "./../Admin/assets/vendors/mdi/css/materialdesignicons.min.css";
import "./../Admin/assets/vendors/ti-icons/css/themify-icons.css";
import "./../Admin/assets/vendors/css/vendor.bundle.base.css";
import "./../Admin/assets/vendors/font-awesome/css/font-awesome.min.css";
import "./../Admin/assets/vendors/bootstrap-datepicker/bootstrap-datepicker.min.css";
import "./../Admin/assets/css/style.css";
import initOffCanvas from "./../Admin/assets/js/off-canvas";
import misc from "./../Admin/assets/js/misc";
import settings from "./../Admin/assets/js/settings";
import todolist from "./../Admin/assets/js/todolist";
import Navbar from "./../Admin/components/Navbar";
import Sidebar from "./components/Sidebar";
import Footer from "./components/Footer";

const EmployerHome = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const toggleMinimize = () => $("body").toggleClass("sidebar-icon-only");
  const toggleMobileSidebar = () => setSidebarOpen(!sidebarOpen);

  // ─── State ────────────────────────────────────────────────
  const [jobs, setJobs] = useState([]);
  const [numJobsInDB, setNumJobsInDB] = useState(0);
  const [totalJobs, setTotalJobs] = useState(0);
  const [jobCurrentPage, setJobCurrentPage] = useState(1);
  const [jobsPerPage, setJobsPerPage] = useState(5);
  const [loading, setLoading] = useState(false);

  const notifyErr = (msg) =>
    toast.error(msg, {
      position: "top-center",
      autoClose: 20000,
      closeOnClick: true,
      pauseOnHover: true,
      theme: "light"
    });

  // ─── Fetch jobs from employer endpoint ──────────────────
  const fetchMyJobs = async () => {
    try {
      setLoading(true);
      const response = await API.get("/employer/jobs", {
        params: { page: jobCurrentPage, limit: jobsPerPage }
      });
      setJobs(response.data.result || []);
      setTotalJobs(response.data.totalJobs || 0);
      setNumJobsInDB(response.data.numJobsInDB || 0);
    } catch (error) {
      console.error(error);
      notifyErr(error.response?.data?.message || "Failed to fetch jobs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMyJobs();
    window.scrollTo({ top: 0 });
  }, [jobCurrentPage, jobsPerPage]);

  // ─── Pagination helpers ──────────────────────────────────
  const totalJobPages = Math.ceil(totalJobs / jobsPerPage);
  const generateJobPageNumbers = () => {
    const max = 5;
    const start = Math.max(1, jobCurrentPage - Math.floor(max / 2));
    const end = Math.min(start + max - 1, totalJobPages);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };
  const nextJobPage = () => {
    if (jobCurrentPage < totalJobPages) setJobCurrentPage(jobCurrentPage + 1);
  };
  const prevJobPage = () => {
    if (jobCurrentPage > 1) setJobCurrentPage(jobCurrentPage - 1);
  };
  const paginateJob = (pageNumber) => setJobCurrentPage(pageNumber);
  const jobPageNumbers = generateJobPageNumbers();

  // ─── Render ──────────────────────────────────────────────
  return (
    <div className="container-scroller">
      <Helmet>
        <title>Employer Dashboard | ThinkBeyond</title>
      </Helmet>
      <Navbar
        toggleMinimize={toggleMinimize}
        toggleMobileSidebar={toggleMobileSidebar}
      />
      <div className="container-fluid page-body-wrapper">
        <Sidebar isOpen={sidebarOpen} />
        <div className="main-panel">
          <div className="content-wrapper">
            <div className="page-header">
              <h3 className="page-title">
                <span className="page-title-icon bg-gradient-primary text-white me-2">
                  <i className="mdi mdi-home"></i>
                </span>{" "}
                Employer Dashboard
              </h3>
              <nav aria-label="breadcrumb">
                <ul className="breadcrumb">
                  <li className="breadcrumb-item active" aria-current="page">
                    <span></span>Overview{" "}
                    <i className="mdi mdi-alert-circle-outline icon-sm text-primary align-middle"></i>
                  </li>
                </ul>
              </nav>
            </div>

            {/* ─── Stats Card ─── */}
            <div className="row">
              <div className="col-md-4 stretch-card grid-margin">
                <div className="card bg-gradient-danger card-img-holder text-white">
                  <div className="card-body">
                    <h4 className="font-weight-normal mb-3">
                      Active Job Openings{" "}
                      <i className="mdi mdi-briefcase mdi-24px float-end"></i>
                    </h4>
                    <h2 className="mb-5">{numJobsInDB}</h2>
                  </div>
                </div>
              </div>
            </div>

            {/* ─── Job List Table ─── */}
            <div className="row">
              <div className="col-12 grid-margin">
                <div className="page-header">
                  <h3 className="page-title">
                    <span className="page-title-icon bg-gradient-primary text-white me-2">
                      <i className="mdi mdi-briefcase"></i>
                    </span>{" "}
                    Active Job List
                  </h3>
                </div>
                <div className="card">
                  <div className="card-body">
                    <h4 className="card-title">List Of Posted Jobs</h4>
                    <div className="table-responsive">
                      <table className="table">
                        <thead>
                          <tr>
                            <th>No.</th>
                            <th>Job Title</th>
                            <th>Qualification</th>
                            <th>Experience</th>
                            <th>Country</th>
                            <th>Work Mode</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {jobs.length > 0 ? (
                            jobs.map((j, index) => (
                              <tr key={j._id}>
                                <td>{index + 1}</td>
                                <td>
                                  <span className="truncate">{j.title}</span>
                                </td>
                                <td>
                                  {j?.jobQualification?.qualificationName}
                                </td>
                                <td>{j?.jobExperience?.workExperienceName}</td>
                                <td>{j?.country?.countryName}</td>
                                <td>{j?.jobModeType?.workModeName}</td>
                                <td>
                                  <Link
                                    className="btn-sm app-btn-secondary rounded-0 me-3"
                                    to={`/Admin/Jobs/View-Job/${j.slug}`}
                                  >
                                    <EyeOutlined />
                                  </Link>
                                  <Link
                                    className="btn-sm app-btn-secondary rounded-0 me-3"
                                    to={`/Admin/Jobs/Update-Job/${j.slug}`}
                                  >
                                    <EditOutlined />
                                  </Link>
                                  <Link
                                    className="btn-sm app-btn-secondary rounded-0 me-3"
                                    to={`/Admin/Jobs/Delete-Job/${j.slug}`}
                                  >
                                    <DeleteOutlined />
                                  </Link>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan={7}>No Jobs Found.</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                    {/* Pagination */}
                    <nav className="app-pagination mt-3">
                      <ul className="pagination justify-content-center">
                        <li className="page-item">
                          <button
                            className="page-link"
                            onClick={prevJobPage}
                            disabled={jobCurrentPage === 1}
                          >
                            Previous
                          </button>
                        </li>
                        {jobPageNumbers.map((number) => (
                          <li
                            key={number}
                            className={
                              jobCurrentPage === number
                                ? "page-item active disabled"
                                : ""
                            }
                          >
                            <button
                              className="page-link"
                              onClick={() => paginateJob(number)}
                            >
                              {number}
                            </button>
                          </li>
                        ))}
                        <li className="page-item">
                          <button
                            className="page-link"
                            onClick={nextJobPage}
                            disabled={jobCurrentPage === totalJobPages}
                          >
                            Next
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <Footer />
        </div>
      </div>
    </div>
  );
};

export default EmployerHome;
